import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@paulownia/shared/components/ui/dialog';
import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@paulownia/shared/components/ui/select';
import { CreatableSelect } from '@paulownia/shared/components/CreatableSelect';
import { InfoTooltip } from '@paulownia/shared';
import { type Product, type ProductOption, type PortionSize, type CompositionComponent, type SaleMode, type WeightEstimate, type ServingEstimate, type Market, type TenantMarket, DAY_OF_WEEK_LABELS } from '@paulownia/shared/types';
import { Textarea } from '@paulownia/shared/components/ui/textarea';
import { Trash2, Loader2, ChevronDown, ChevronRight, X, Plus, Scale, Package, Users, PackageOpen, ShoppingBasket, MapPin } from 'lucide-react';
import { cn } from '@paulownia/shared/lib/utils';
import { OptionEditor } from './components/OptionEditor';
import { ImageUploader } from './components/ImageUploader';
import { reportError } from '@paulownia/shared/lib/error-reporting';
import { toast } from 'sonner';

// Helper to generate camelCase ID from label
function toCamelCase(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .toLowerCase()
    .replace(/[^a-z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
    .replace(/[^a-zA-Z0-9]/g, '')
    .replace(/^./, (chr) => chr.toLowerCase());
}

// Helper to capitalize first letter
function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// SaleMode display configuration
const SALE_MODE_CONFIG: Record<SaleMode, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  UNIT: { label: 'Pièce prix fixe', icon: Package },
  UNIT_PRICED_BY_WEIGHT: { label: 'Pièce facturée au poids', icon: ShoppingBasket },
  WEIGHT: { label: 'Poids libre', icon: Scale },
  WEIGHT_ESTIMATED: { label: 'Poids par personnes', icon: Users },
  WEIGHT_PACKAGED: { label: 'Formats fixes', icon: PackageOpen },
};

interface ProductEditDialogProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: Product) => Promise<void>;
  onDelete?: (productId: string) => Promise<void>;
  sections: string[];
  subSectionsBySection?: Record<string, string[]>; // SubSections grouped by section
  existingOptions: ProductOption[]; // All existing options from all products (with full configuration)
  allProducts?: Product[]; // All products for composition selection
  isNew?: boolean;
  tenantId?: string; // Required for image upload
  tenantMarkets?: (TenantMarket & { market: Market })[]; // Markets for availability selection
}

export function ProductEditDialog({
  product,
  isOpen,
  onClose,
  onSave,
  onDelete,
  sections,
  subSectionsBySection = {},
  existingOptions,
  allProducts = [],
  isNew = false,
  tenantId,
  tenantMarkets = [],
}: ProductEditDialogProps) {
  const [formData, setFormData] = useState<Partial<Product>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [optionsExpanded, setOptionsExpanded] = useState(false);
  const [portionSizesExpanded, setPortionSizesExpanded] = useState(false);
  const [compositionExpanded, setCompositionExpanded] = useState(false);
  const [saleModeExpanded, setSaleModeExpanded] = useState(false);
  // State for option being edited (null = not editing, index = editing existing)
  const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(null);
  const [editingOption, setEditingOption] = useState<ProductOption | null>(null);

  // Reset form when product changes
  useEffect(() => {
    if (product) {
      setFormData({ ...product });
    } else {
      setFormData({
        name: '',
        section: '',
        subSection: '',
        shortenName: '',
        description: '',
        picture: '',
        price: undefined,
        saleMode: 'UNIT',
        availableOptions: [],
        portionSizes: [],
      });
    }
    setShowDeleteConfirm(false);
    // Reset option editing state
    setEditingOptionIndex(null);
    setEditingOption(null);
    // Auto-expand options if product has options OR there are existing options to add
    const hasProductOptions = (product?.availableOptions?.length ?? 0) > 0;
    const hasExistingOptions = existingOptions.length > 0;
    setOptionsExpanded(hasProductOptions || hasExistingOptions);
    // Auto-expand portion sizes if product has WEIGHT_PACKAGED saleMode and has portion sizes
    setPortionSizesExpanded(product?.saleMode === 'WEIGHT_PACKAGED' && (product?.portionSizes?.length ?? 0) > 0);
    // Auto-expand composition if product has a composition option
    const hasComposition = product?.availableOptions?.some(opt => opt.type === 'composition') ?? false;
    setCompositionExpanded(hasComposition);
    // Auto-expand saleMode if product has saleMode-specific fields
    const hasSaleModeFields = product?.saleMode || product?.weightEstimate || product?.servingEstimate || product?.disclaimer;
    setSaleModeExpanded(!!hasSaleModeFields);
  }, [product, isOpen, existingOptions.length]);

  // Options management
  const startNewOption = (label: string) => {
    if (!label.trim()) return;

    const id = toCamelCase(label);
    const currentOptions = formData.availableOptions || [];

    // Prevent duplicate IDs
    if (currentOptions.some(opt => opt.id === id)) {
      return;
    }

    // Check if this option exists in other products (to copy its configuration)
    const existingOption = existingOptions.find(opt => opt.label === label.trim());

    // Create new option - copy from existing or create fresh
    const newOption: ProductOption = existingOption
      ? {
          ...existingOption,
          id, // Generate new ID based on label
          choices: existingOption.choices ? [...existingOption.choices] : undefined,
        }
      : {
          id,
          label: label.trim(),
          type: 'boolean',
        };

    setEditingOption(newOption);
    setEditingOptionIndex(-1); // -1 indicates new option
  };

  const startEditOption = (index: number) => {
    const opt = formData.availableOptions?.[index];
    if (opt) {
      setEditingOption({ ...opt, choices: opt.choices ? [...opt.choices] : undefined });
      setEditingOptionIndex(index);
    }
  };

  const saveEditingOption = () => {
    if (!editingOption) return;

    // Validate select/multiselect have choices
    if ((editingOption.type === 'select' || editingOption.type === 'multiselect') &&
        (!editingOption.choices || editingOption.choices.length === 0)) {
      return; // Don't save without choices
    }

    setFormData(prev => {
      const currentOptions = prev.availableOptions || [];
      if (editingOptionIndex === -1) {
        // New option
        return { ...prev, availableOptions: [...currentOptions, editingOption] };
      } else if (editingOptionIndex !== null) {
        // Edit existing
        return {
          ...prev,
          availableOptions: currentOptions.map((opt, i) =>
            i === editingOptionIndex ? editingOption : opt
          ),
        };
      }
      return prev;
    });

    setEditingOption(null);
    setEditingOptionIndex(null);
  };

  const cancelEditOption = () => {
    setEditingOption(null);
    setEditingOptionIndex(null);
  };

  const removeOption = (optionId: string) => {
    setFormData(prev => ({
      ...prev,
      availableOptions: (prev.availableOptions || []).filter(opt => opt.id !== optionId),
    }));
  };

  // Filter out options already added to this product (extract labels for CreatableSelect)
  // Also filter out composition options - they have their own dedicated section
  const availableOptionsToAdd = existingOptions
    .filter(opt => opt.type !== 'composition')
    .filter(opt => !(formData.availableOptions || []).some(added => added.label === opt.label))
    .map(opt => opt.label);

  // Options to display in the Options section (excluding composition which has its own section)
  const displayableOptions = (formData.availableOptions || []).filter(opt => opt.type !== 'composition');

  // Helper to get option type label
  const getOptionTypeLabel = (type: ProductOption['type']) => {
    switch (type) {
      case 'boolean': return 'Oui/Non';
      case 'select': return 'Choix unique';
      case 'multiselect': return 'Choix multiples';
    }
  };

  // Portion sizes management
  const addPortionSize = () => {
    const newId = `portion_${Date.now()}`;
    const newSize: PortionSize = {
      id: newId,
      label: '',
      price: 0,
    };
    setFormData(prev => ({
      ...prev,
      portionSizes: [...(prev.portionSizes || []), newSize],
    }));
  };

  const updatePortionSize = (index: number, updates: Partial<PortionSize>) => {
    setFormData(prev => ({
      ...prev,
      portionSizes: (prev.portionSizes || []).map((size, i) =>
        i === index ? { ...size, ...updates } : size
      ),
    }));
  };

  const removePortionSize = (index: number) => {
    setFormData(prev => ({
      ...prev,
      portionSizes: (prev.portionSizes || []).filter((_, i) => i !== index),
    }));
  };

  // Composition management
  const getCompositionOption = (): ProductOption | undefined => {
    return formData.availableOptions?.find(opt => opt.type === 'composition');
  };

  const initializeComposition = () => {
    const existingComposition = getCompositionOption();
    if (existingComposition) return; // Already has composition

    const compositionOption: ProductOption = {
      id: 'composition',
      label: 'Composition',
      type: 'composition',
      components: [],
      totalRequired: 1,
    };
    setFormData(prev => ({
      ...prev,
      availableOptions: [...(prev.availableOptions || []), compositionOption],
    }));
  };

  const removeComposition = () => {
    setFormData(prev => ({
      ...prev,
      availableOptions: (prev.availableOptions || []).filter(opt => opt.type !== 'composition'),
    }));
  };

  const updateCompositionTotalRequired = (total: number) => {
    setFormData(prev => ({
      ...prev,
      availableOptions: (prev.availableOptions || []).map(opt =>
        opt.type === 'composition' ? { ...opt, totalRequired: total } : opt
      ),
    }));
  };

  const addCompositionComponent = (productId: string) => {
    const productToAdd = allProducts.find(p => p.id === productId);
    if (!productToAdd) return;

    const newComponent: CompositionComponent = {
      productId: productToAdd.id,
      label: productToAdd.name,
    };

    setFormData(prev => ({
      ...prev,
      availableOptions: (prev.availableOptions || []).map(opt => {
        if (opt.type === 'composition') {
          const existingComponents = opt.components || [];
          // Avoid duplicates
          if (existingComponents.some(c => c.productId === productId)) {
            return opt;
          }
          return { ...opt, components: [...existingComponents, newComponent] };
        }
        return opt;
      }),
    }));
  };

  const removeCompositionComponent = (productId: string) => {
    setFormData(prev => ({
      ...prev,
      availableOptions: (prev.availableOptions || []).map(opt => {
        if (opt.type === 'composition') {
          return {
            ...opt,
            components: (opt.components || []).filter(c => c.productId !== productId),
          };
        }
        return opt;
      }),
    }));
  };

  // Get products available for composition (exclude current product)
  const availableProductsForComposition = allProducts.filter(p => {
    if (product && p.id === product.id) return false; // Exclude self
    const compositionOption = getCompositionOption();
    if (compositionOption?.components?.some(c => c.productId === p.id)) return false; // Already added
    return true;
  });

  const handleSave = async () => {
    if (!formData.name?.trim() || !formData.section?.trim()) {
      return;
    }

    // Filter out empty portion sizes and generate proper IDs
    const validPortionSizes = (formData.portionSizes || [])
      .filter(p => p.label.trim())
      .map(p => ({
        id: toCamelCase(p.label) || p.id,
        label: p.label.trim(),
        price: p.price,
      }));

    const sectionValue = formData.section.trim().toLowerCase();

    setSaving(true);
    try {
      await onSave({
        id: product?.id || '',
        name: formData.name.trim(),
        section: sectionValue,
        subSection: formData.subSection?.trim().toLowerCase() || undefined,
        shortenName: formData.shortenName?.trim() || undefined,
        description: formData.description?.trim() || undefined,
        picture: formData.picture?.trim() || undefined,
        imageUrl: formData.imageUrl,
        thumbnailUrl: formData.thumbnailUrl,
        imageUpdatedAt: formData.imageUpdatedAt,
        price: formData.price,
        // SaleMode fields
        saleMode: formData.saleMode || 'UNIT',
        weightEstimate: formData.weightEstimate,
        servingEstimate: formData.servingEstimate,
        disclaimer: formData.disclaimer?.trim() || undefined,
        // Other fields
        availableOptions: formData.availableOptions,
        portionSizes: validPortionSizes.length > 0 ? validPortionSizes : undefined,
        displayOrder: formData.displayOrder,
        // Market availability
        marketIds: formData.marketIds && formData.marketIds.length > 0 ? formData.marketIds : undefined,
      } as Product);
      onClose();
    } catch (error) {
      reportError(error, { context: 'ProductEditDialog.handleSave', productId: product?.id });
      toast.error('Erreur lors de la sauvegarde du produit');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!product?.id || !onDelete) return;

    setDeleting(true);
    try {
      await onDelete(product.id);
      onClose();
    } catch (error) {
      reportError(error, { context: 'ProductEditDialog.handleDelete', productId: product?.id });
      toast.error('Erreur lors de la suppression du produit');
    } finally {
      setDeleting(false);
    }
  };

  const updateField = <K extends keyof Product>(field: K, value: Product[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col overflow-x-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {isNew ? 'Nouveau produit' : 'Modifier le produit'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0 px-1">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Nom du produit *</Label>
            <Input
              id="name"
              value={formData.name || ''}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="ex: Poulet rôti entier"
            />
          </div>

          {/* Section (primary) */}
          <div className="space-y-2">
            <Label>Section *</Label>
            <CreatableSelect
              value={formData.section || ''}
              onChange={(v) => {
                updateField('section', v);
                // Reset subSection when section changes
                if (v !== formData.section) {
                  updateField('subSection', '');
                }
              }}
              options={sections}
              placeholder="Sélectionner ou créer..."
              formatDisplay={capitalize}
            />
          </div>

          {/* SubSection - only shown when section is selected */}
          {formData.section && (
            <div className="space-y-2">
              <Label>Sous-section</Label>
              <CreatableSelect
                value={formData.subSection || ''}
                onChange={(v) => updateField('subSection', v)}
                options={subSectionsBySection[formData.section.toLowerCase()] || []}
                placeholder="Sélectionner ou créer..."
                formatDisplay={capitalize}
              />
            </div>
          )}

          {/* Short Name */}
          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="shortenName">Nom court</Label>
              <InfoTooltip content="Utilisé lors de la création de commande pour un affichage plus compact" />
            </div>
            <Input
              id="shortenName"
              value={formData.shortenName || ''}
              onChange={(e) => updateField('shortenName', e.target.value)}
              placeholder="ex: Poulet"
            />
          </div>

          {/* Price */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price">Prix TTC (€)</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={formData.price ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  updateField('price', val === '' ? undefined : parseFloat(val));
                }}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label>Mode de vente</Label>
              <Select
                value={formData.saleMode || 'UNIT'}
                onValueChange={(v) => updateField('saleMode', v as SaleMode)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SALE_MODE_CONFIG).map(([mode, config]) => (
                    <SelectItem key={mode} value={mode}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (visible clients)</Label>
            <Input
              id="description"
              value={formData.description || ''}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Description courte du produit"
            />
          </div>

          {/* Market availability - only show when merchant has multiple markets */}
          {tenantMarkets.length > 1 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-orange-600" />
                  Disponibilité par marché
                </Label>
                <InfoTooltip content="Sélectionnez les marchés où ce produit est disponible. Si aucun marché n'est coché, le produit sera disponible partout." />
              </div>
              <div className="space-y-1.5 rounded-lg border p-3 bg-gray-50/50">
                {tenantMarkets.map(tm => {
                  const isChecked = formData.marketIds?.includes(tm.marketId) ?? false;
                  const allMarketsMode = !formData.marketIds || formData.marketIds.length === 0;
                  return (
                    <label
                      key={tm.marketId}
                      className="flex items-center gap-2.5 cursor-pointer py-1"
                    >
                      <input
                        type="checkbox"
                        checked={allMarketsMode || isChecked}
                        onChange={(e) => {
                          if (allMarketsMode) {
                            // Switching from "all markets" to specific: check only this one
                            if (!e.target.checked) {
                              // Unchecking in "all" mode → select all EXCEPT this one
                              const allExceptThis = tenantMarkets
                                .filter(m => m.marketId !== tm.marketId)
                                .map(m => m.marketId);
                              updateField('marketIds', allExceptThis);
                            }
                          } else {
                            const currentIds = formData.marketIds || [];
                            if (e.target.checked) {
                              const newIds = [...currentIds, tm.marketId];
                              // If all markets are selected, clear to "all" mode
                              if (newIds.length >= tenantMarkets.length) {
                                updateField('marketIds', undefined);
                              } else {
                                updateField('marketIds', newIds);
                              }
                            } else {
                              const newIds = currentIds.filter(id => id !== tm.marketId);
                              if (newIds.length === 0) {
                                // Can't uncheck all - revert to all markets
                                updateField('marketIds', undefined);
                              } else {
                                updateField('marketIds', newIds);
                              }
                            }
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm">
                        {tm.market.city} - {DAY_OF_WEEK_LABELS[tm.market.dayOfWeek]}
                      </span>
                    </label>
                  );
                })}
                {(!formData.marketIds || formData.marketIds.length === 0) && (
                  <p className="text-xs text-gray-500 mt-1">Tous les marchés (par défaut)</p>
                )}
              </div>
            </div>
          )}

          {/* Image Upload - only show if product has an ID and tenantId is provided */}
          {!isNew && product?.id && tenantId ? (
            <ImageUploader
              tenantId={tenantId}
              productId={product.id}
              currentImageUrl={formData.imageUrl}
              currentThumbnailUrl={formData.thumbnailUrl}
              currentPictureUrl={formData.picture}
              onImageUploaded={(imageUrl, thumbnailUrl) => {
                setFormData(prev => ({
                  ...prev,
                  imageUrl,
                  thumbnailUrl,
                  imageUpdatedAt: new Date().toISOString(),
                  picture: undefined, // Clear legacy URL when image is uploaded
                }));
              }}
              onImageDeleted={() => {
                setFormData(prev => ({
                  ...prev,
                  imageUrl: undefined,
                  thumbnailUrl: undefined,
                  imageUpdatedAt: undefined,
                }));
              }}
              onPictureUrlChange={(url) => updateField('picture', url)}
            />
          ) : (
            // Fallback to URL input for new products (no product ID yet)
            <div className="space-y-2">
              <Label htmlFor="picture">URL de l'image</Label>
              <Input
                id="picture"
                value={formData.picture || ''}
                onChange={(e) => updateField('picture', e.target.value)}
                placeholder="https://..."
              />
              {isNew && (
                <p className="text-xs text-gray-500">
                  Vous pourrez telecharger une photo apres avoir cree le produit.
                </p>
              )}
            </div>
          )}

          {/* Collapsible SaleMode Section (only if product has saleMode) */}
          {(formData.saleMode || formData.weightEstimate || formData.servingEstimate || formData.disclaimer) && (
            <div className={cn("border rounded-lg", saleModeExpanded ? "overflow-visible" : "overflow-hidden")}>
              <button
                type="button"
                onClick={() => setSaleModeExpanded(!saleModeExpanded)}
                className={cn(
                  'w-full flex items-center justify-between p-3',
                  'text-left font-medium text-gray-700 hover:bg-gray-50 transition-colors',
                  saleModeExpanded && 'border-b bg-gray-50'
                )}
              >
                <span className="flex items-center gap-2">
                  Mode de vente
                  {formData.saleMode && (
                    <span className="px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                      {SALE_MODE_CONFIG[formData.saleMode]?.label || formData.saleMode}
                    </span>
                  )}
                </span>
                {saleModeExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {saleModeExpanded && (
                <div className="p-3 space-y-4 bg-gray-50/50">
                  {/* SaleMode Display */}
                  {formData.saleMode && (
                    <div className="flex items-center gap-2 p-2 bg-white rounded-lg border">
                      {(() => {
                        const config = SALE_MODE_CONFIG[formData.saleMode];
                        const Icon = config.icon;
                        return (
                          <>
                            <Icon className="h-5 w-5 text-blue-600" />
                            <span className="font-medium text-sm">{config.label}</span>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* WeightEstimate fields for UNIT_PRICED_BY_WEIGHT */}
                  {formData.saleMode === 'UNIT_PRICED_BY_WEIGHT' && (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Poids moyen (kg)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={formData.weightEstimate?.averageWeight ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            updateField('weightEstimate', {
                              ...formData.weightEstimate,
                              averageWeight: val === '' ? 0 : parseFloat(val),
                            } as WeightEstimate);
                          }}
                          placeholder="ex: 1.2"
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Portions par pièce (optionnel)</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            value={formData.weightEstimate?.servesPerUnit?.min ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              const current = formData.weightEstimate || { averageWeight: 0 };
                              updateField('weightEstimate', {
                                ...current,
                                servesPerUnit: {
                                  ...current.servesPerUnit,
                                  min: val === '' ? undefined : parseInt(val, 10),
                                  max: current.servesPerUnit?.max,
                                },
                              } as WeightEstimate);
                            }}
                            placeholder="Min"
                            className="h-9 w-20"
                          />
                          <span className="text-gray-400">à</span>
                          <Input
                            type="number"
                            min="1"
                            value={formData.weightEstimate?.servesPerUnit?.max ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              const current = formData.weightEstimate || { averageWeight: 0 };
                              updateField('weightEstimate', {
                                ...current,
                                servesPerUnit: {
                                  ...current.servesPerUnit,
                                  min: current.servesPerUnit?.min,
                                  max: val === '' ? undefined : parseInt(val, 10),
                                },
                              } as WeightEstimate);
                            }}
                            placeholder="Max"
                            className="h-9 w-20"
                          />
                          <span className="text-gray-500 text-sm">personnes</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ServingEstimate fields for WEIGHT_ESTIMATED */}
                  {formData.saleMode === 'WEIGHT_ESTIMATED' && (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Grammes par personne</Label>
                        <Input
                          type="number"
                          step="10"
                          min="50"
                          value={formData.servingEstimate?.gramsPerPerson ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            updateField('servingEstimate', {
                              gramsPerPerson: val === '' ? 200 : parseInt(val, 10),
                            } as ServingEstimate);
                          }}
                          placeholder="ex: 200"
                          className="h-9 w-32"
                        />
                        <p className="text-xs text-gray-500">
                          Le client choisira le nombre de personnes lors de sa commande.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Disclaimer field */}
                  {(formData.saleMode === 'UNIT_PRICED_BY_WEIGHT' || formData.saleMode === 'WEIGHT_ESTIMATED') && (
                    <div className="space-y-1">
                      <Label className="text-sm">Message au client</Label>
                      <Textarea
                        value={formData.disclaimer || ''}
                        onChange={(e) => updateField('disclaimer', e.target.value || undefined)}
                        placeholder="ex: Prix final selon poids réel"
                        rows={2}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Collapsible Composition Section - Only for UNIT saleMode */}
          {formData.saleMode === 'UNIT' && allProducts.length > 0 && (
            <div className={cn("border rounded-lg", compositionExpanded ? "overflow-visible" : "overflow-hidden")}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setCompositionExpanded(!compositionExpanded)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCompositionExpanded(!compositionExpanded); } }}
                className={cn(
                  'w-full flex items-center justify-between p-3 cursor-pointer',
                  'text-left font-medium text-gray-700 hover:bg-gray-50 transition-colors',
                  compositionExpanded && 'border-b bg-gray-50'
                )}
              >
                <span className="flex items-center gap-2">
                  Composition (coffret)
                  <InfoTooltip content="Permet de créer un produit composable (ex: coffret de macarons) où le client choisit lui-même les éléments parmi une liste de produits que vous définissez." />
                  {getCompositionOption() && (
                    <span className="px-1.5 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800">
                      {getCompositionOption()?.components?.length ?? 0} produits
                    </span>
                  )}
                </span>
                {compositionExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </div>

              {compositionExpanded && (
                <div className="p-3 space-y-3 bg-gray-50/50">
                  <p className="text-xs text-gray-500">
                    Permet de créer un coffret composable où le client choisit les produits (ex: coffret de macarons).
                  </p>

                  {!getCompositionOption() ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={initializeComposition}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Activer la composition
                    </Button>
                  ) : (
                    <>
                      {/* Total required */}
                      <div className="space-y-1">
                        <Label className="text-xs">Nombre total requis</Label>
                        <Input
                          type="number"
                          min="1"
                          value={getCompositionOption()?.totalRequired ?? 1}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            if (!isNaN(val) && val > 0) {
                              updateCompositionTotalRequired(val);
                            }
                          }}
                          placeholder="ex: 6, 12, 24..."
                          className="h-9 w-32"
                        />
                        <p className="text-xs text-gray-400">
                          Le client devra sélectionner exactement ce nombre de produits.
                        </p>
                      </div>

                      {/* List of components */}
                      <div className="space-y-2">
                        <Label className="text-xs">Produits disponibles dans le coffret</Label>
                        {(getCompositionOption()?.components?.length ?? 0) > 0 ? (
                          <div className="space-y-1">
                            {getCompositionOption()?.components?.map((comp) => (
                              <div
                                key={comp.productId}
                                className="flex items-center justify-between p-2 rounded-lg bg-white border"
                              >
                                <span className="text-sm">{comp.label}</span>
                                <button
                                  type="button"
                                  onClick={() => removeCompositionComponent(comp.productId)}
                                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic">Aucun produit ajouté</p>
                        )}
                      </div>

                      {/* Add product selector */}
                      {availableProductsForComposition.length > 0 && (
                        <div className="pt-2 border-t">
                          <Label className="text-xs text-gray-600 mb-1.5 block">Ajouter un produit</Label>
                          <Select onValueChange={addCompositionComponent}>
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Sélectionner un produit..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableProductsForComposition.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name}
                                  {p.section && (
                                    <span className="text-gray-400 ml-2">({capitalize(p.section)})</span>
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Remove composition button */}
                      <div className="pt-2 border-t">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={removeComposition}
                          className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Désactiver la composition
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Collapsible Portion Sizes Section - Only for WEIGHT_PACKAGED */}
          {formData.saleMode === 'WEIGHT_PACKAGED' && (
            <div className={cn("border rounded-lg", portionSizesExpanded ? "overflow-visible" : "overflow-hidden")}>
              <button
                type="button"
                onClick={() => setPortionSizesExpanded(!portionSizesExpanded)}
                className={cn(
                  'w-full flex items-center justify-between p-3',
                  'text-left font-medium text-gray-700 hover:bg-gray-50 transition-colors',
                  portionSizesExpanded && 'border-b bg-gray-50'
                )}
              >
                <span className="flex items-center gap-2">
                  Formats de vente
                  {(formData.portionSizes?.length ?? 0) > 0 && (
                    <span className="px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                      {formData.portionSizes?.length}
                    </span>
                  )}
                </span>
                {portionSizesExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {portionSizesExpanded && (
                <div className="p-3 space-y-3 bg-gray-50/50">
                  <p className="text-xs text-gray-500">
                    Définissez des formats prédéfinis (ex: 250g, 500g, 1kg) avec leur prix fixe.
                  </p>

                  {/* List of portion sizes */}
                  {formData.portionSizes && formData.portionSizes.length > 0 ? (
                    <div className="space-y-3">
                      {formData.portionSizes.map((portion, index) => (
                        <div key={portion.id} className="p-3 bg-white rounded-lg border space-y-2">
                          {/* Header with delete button */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500">Format {index + 1}</span>
                            <button
                              type="button"
                              onClick={() => removePortionSize(index)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title="Supprimer"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="flex gap-3">
                            {/* Label */}
                            <div className="flex-1 space-y-1">
                              <Label className="text-xs">Nom</Label>
                              <Input
                                value={portion.label}
                                onChange={(e) => updatePortionSize(index, { label: e.target.value })}
                                placeholder="ex: 250g, Petit, 1kg..."
                                className="h-9"
                              />
                            </div>
                            {/* Price */}
                            <div className="w-28 space-y-1">
                              <Label className="text-xs">Prix (€)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={portion.price ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  updatePortionSize(index, {
                                    price: val === '' ? 0 : parseFloat(val),
                                  });
                                }}
                                placeholder="0.00"
                                className="h-9"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Aucun format défini</p>
                  )}

                  {/* Add button */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addPortionSize}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter un format
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Collapsible Options Section */}
          <div className={cn("border rounded-lg", optionsExpanded ? "overflow-visible" : "overflow-hidden")}>
            <button
              type="button"
              onClick={() => setOptionsExpanded(!optionsExpanded)}
              className={cn(
                'w-full flex items-center justify-between p-3',
                'text-left font-medium text-gray-700 hover:bg-gray-50 transition-colors',
                optionsExpanded && 'border-b bg-gray-50'
              )}
            >
              <span className="flex items-center gap-2">
                Options disponibles
                {displayableOptions.length > 0 && (
                  <span className="px-1.5 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800">
                    {displayableOptions.length}
                  </span>
                )}
              </span>
              {optionsExpanded ? (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400" />
              )}
            </button>

            {optionsExpanded && (
              <div className="p-3 space-y-3 bg-gray-50/50 relative">
                {/* Option Editor */}
                {editingOption && (
                  <OptionEditor
                    option={editingOption}
                    isNew={editingOptionIndex === -1}
                    onChange={setEditingOption}
                    onSave={saveEditingOption}
                    onCancel={cancelEditOption}
                  />
                )}

                {/* Current Options list */}
                {!editingOption && (
                  <>
                    {displayableOptions.length > 0 ? (
                      <div className="space-y-2">
                        {displayableOptions.map((opt) => {
                          // Find the actual index in the full availableOptions array for editing
                          const actualIndex = (formData.availableOptions || []).findIndex(o => o.id === opt.id);
                          return (
                          <div
                            key={opt.id}
                            className="flex items-center justify-between p-2 rounded-lg bg-white border"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm truncate">{opt.label}</span>
                                <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                  {getOptionTypeLabel(opt.type)}
                                </span>
                                {opt.type === 'select' && opt.required && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600">
                                    Obligatoire
                                  </span>
                                )}
                              </div>
                              {opt.choices && opt.choices.length > 0 && (
                                <div className="text-xs text-gray-500 mt-0.5 truncate">
                                  {opt.choices.map(c => c.label).join(', ')}
                                  {opt.maxSelections && ` (max ${opt.maxSelections})`}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <button
                                type="button"
                                onClick={() => startEditOption(actualIndex)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Modifier"
                              >
                                <ChevronRight className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeOption(opt.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                                title="Supprimer"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Aucune option définie</p>
                    )}

                    {/* Add New Option using CreatableSelect */}
                    <div className="pt-2 border-t">
                      <Label className="text-sm text-gray-600 mb-1.5 block">Ajouter une option</Label>
                      <CreatableSelect
                        value=""
                        onChange={(label) => {
                          if (label) startNewOption(label);
                        }}
                        options={availableOptionsToAdd}
                        placeholder="Sélectionner ou créer..."
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Save/Cancel buttons */}
          <div className="flex gap-2 justify-end w-full pt-4">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !formData.name?.trim() || !formData.section?.trim()}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {isNew ? 'Créer' : 'Enregistrer'}
            </Button>
          </div>
        </div>

        {/* Delete button in footer */}
        {!isNew && onDelete && (
          <DialogFooter className="flex-shrink-0 pt-4 border-t">
            {showDeleteConfirm ? (
              <div className="flex items-center justify-center gap-2 w-full">
                <span className="text-sm text-red-600">Supprimer ce produit ?</span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Oui'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Non
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer ce produit
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
